import React, { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import FormattingInput from 'components/FormattingInput';
import Inputs from 'components/Inputs';
import MetricsInfoButton from 'components/MetricsInfoButton';
import getTermColor from 'components/MetricsInfoButton/getTermColor';
import { ErrorMessage } from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';

import MetricFormStyles from '../styles';
import { getMetricLetter, toExpressionParts } from './expressionAliases';
import InputMetrics from './InputMetrics';

const ExpressionPreviewDiv = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: var(--color-bg-tag);
  overflow-wrap: anywhere;
`;

const prettifyOperators = (text: string) =>
  text.replaceAll('*', ' × ').replaceAll('/', ' ÷ ').replace(/\s+/g, ' ');

const ExpressionPreview = ({
  expression,
  metricsByIndex,
}: {
  expression: string;
  metricsByIndex: (Metrics.NormalMetric | undefined)[];
}) => {
  const parts = toExpressionParts({
    letterExpression: expression,
    metricCount: metricsByIndex.length,
  });

  return (
    <ExpressionPreviewDiv data-testid="expression-preview">
      <Typography.Body type="Body 12">
        {parts.map((part, index) => {
          if (part.type === 'text') {
            return (
              <React.Fragment key={index}>
                {prettifyOperators(part.text)}
              </React.Fragment>
            );
          }

          const metric = metricsByIndex[part.metricIndex];
          return (
            <span
              key={index}
              style={{
                color: getTermColor(part.metricIndex),
                fontWeight: 600,
              }}
            >
              {metric ? metric.name : part.letter}
            </span>
          );
        })}
      </Typography.Body>
    </ExpressionPreviewDiv>
  );
};

const CompoundMetricForm = ({
  name,
  onNameChanged,
  description,
  onDescriptionChanged,
  selectedMetricIds,
  selectedMetrics,
  onAddMetric,
  onRemoveMetric,
  expression,
  onExpressionChanged,
  expressionError,
  formatting,
  setFormatting,
  onMetricPickerOpened,
}: {
  name: string;
  onNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  description: string;
  onDescriptionChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  selectedMetricIds: string[];
  selectedMetrics: Metrics.NormalMetric[];
  onAddMetric: (mId: string) => void;
  onRemoveMetric: (mId: string) => void;
  // The expression with input metrics shown as letters (A, B...)
  expression: string;
  onExpressionChanged: (expression: string) => void;
  expressionError?: string;
  formatting: MetricFormatting;
  setFormatting: React.Dispatch<React.SetStateAction<MetricFormatting>>;
  onMetricPickerOpened: () => void;
}) => {
  const expressionInputRef = useRef<HTMLInputElement | null>(null);

  // Indexed by position in the expression, so letters line up with metrics
  const metricsByIndex = useMemo(
    () =>
      selectedMetricIds.map((metricId) =>
        selectedMetrics.find((m) => m.id === metricId),
      ),
    [selectedMetricIds, selectedMetrics],
  );

  const unusedMetricWarnings = useMemo(() => {
    if (expression.trim() === '' || expressionError) {
      return [];
    }

    const usedIndexes = new Set(
      toExpressionParts({
        letterExpression: expression,
        metricCount: metricsByIndex.length,
      }).flatMap((part) => (part.type === 'metric' ? [part.metricIndex] : [])),
    );
    return metricsByIndex.flatMap((metric, index) =>
      metric && !usedIndexes.has(index)
        ? [`${getMetricLetter(index)} (${metric.name}) isn't used.`]
        : [],
    );
  }, [expression, expressionError, metricsByIndex]);

  const onExpressionInputChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onExpressionChanged(event.target.value);
    },
    [onExpressionChanged],
  );

  // Inserts the metric's letter at the caret, or at the end if the
  // expression input isn't focused
  const onInsertMetric = useCallback(
    (letter: string) => {
      const input = expressionInputRef.current;
      const isFocused = !!input && document.activeElement === input;
      const start =
        isFocused && input.selectionStart !== null
          ? input.selectionStart
          : expression.length;
      const end =
        isFocused && input.selectionEnd !== null ? input.selectionEnd : start;

      onExpressionChanged(
        `${expression.slice(0, start)}${letter}${expression.slice(end)}`,
      );

      requestAnimationFrame(() => {
        if (!input) {
          return;
        }
        input.focus();
        const caret = start + letter.length;
        input.setSelectionRange(caret, caret);
      });
    },
    [expression, onExpressionChanged],
  );

  return (
    <MetricFormStyles.OuterDiv data-testid="edit-compound-metric-form">
      <MetricFormStyles.InnerDiv>
        <div style={{ marginBottom: 16 }}>
          <Typography.Body type="Label">Name</Typography.Body>
          <Inputs.TextInput
            autoFocus
            value={name}
            onChange={onNameChanged}
            data-testid={'name'}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Body type="Label">Description</Typography.Body>
          <Inputs.TextInput
            value={description}
            onChange={onDescriptionChanged}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <InputMetrics
            selectedMetricIds={selectedMetricIds}
            selectedMetrics={selectedMetrics}
            onAddMetric={onAddMetric}
            onRemoveMetric={onRemoveMetric}
            onInsertMetric={onInsertMetric}
            onOpened={onMetricPickerOpened}
          />
        </div>
        <div style={{ marginBottom: 24 }}>
          <Row spaceBetween centerAlign>
            <Typography.Body type="Label">Expression</Typography.Body>
            <MetricsInfoButton />
          </Row>
          <Inputs.TextInput
            value={expression}
            onChange={onExpressionInputChanged}
            forwardRef={expressionInputRef}
            state={expressionError ? 'Error' : undefined}
            placeholder={'e.g. (A + B) / C * 100'}
            data-testid={'expression'}
          />
          {expressionError && <ErrorMessage message={expressionError} />}
          {!expressionError && expression.trim() !== '' && (
            <ExpressionPreview
              expression={expression}
              metricsByIndex={metricsByIndex}
            />
          )}
          {unusedMetricWarnings.map((warning) => (
            <div key={warning} style={{ marginTop: 4 }}>
              <Typography.Body
                type="Body 12"
                color="var(--color-status-warning)"
              >
                {warning}
              </Typography.Body>
            </div>
          ))}
          {expression.trim() === '' && (
            <div style={{ marginTop: 4 }}>
              <Typography.Body type="Body 12" color="var(--color-text-muted)">
                Refer to input metrics by their letter. Click a letter to insert
                it.
              </Typography.Body>
            </div>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <Typography.Body type="Label">Formatting (optional)</Typography.Body>
          <FormattingInput
            formatting={formatting}
            setFormatting={setFormatting}
          />
        </div>
      </MetricFormStyles.InnerDiv>
    </MetricFormStyles.OuterDiv>
  );
};

export default CompoundMetricForm;
